import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import { AttachCircle, MessageText1 } from 'iconsax-react';
import InfoText from '@/components/atoms/InfoText';
import { Proposal } from './PostsListWrapper';
import BasicProfileCard from './BasicProfileCard';
import type { PostDetailsCardTranslator } from './postDetailsCardTranslations';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export default function BuyerProposalBasicCard({
  proposal,
  t,
  currencyDisplay = 'SAR',
  locale = 'en',
  withPadding = true,
}: {
  proposal: Proposal;
  profileActions?: React.ReactElement;
  cardActions?: React.ReactElement;
  evalCard?: React.ReactElement;
  withoutMargin?: boolean;
  tags?: { name: string; code: string }[];
  t: PostDetailsCardTranslator;
  canProfileRoute?: boolean;
  withBg?: boolean;
  withPadding?: boolean;
  locale?: string;
  currencyDisplay?: string;
}) {
  const price = proposal?.totalPriceConverted ?? proposal?.totalPrice;
  const formattedPrice =
    price !== undefined && price !== null
      ? Number(price).toLocaleString('en-US', {
          maximumFractionDigits: 0,
        })
      : '-';

  const delivery = proposal?.deliveryTime;
  let formattedDelivery = '-';
  if (delivery !== undefined && delivery !== null) {
    const num = typeof delivery === 'number' ? delivery : parseInt(String(delivery), 10);
    if (!isNaN(num)) {
      formattedDelivery = t('postDetails.deliveryDays', { count: num });
    } else {
      formattedDelivery = String(delivery);
    }
  }

  const attachments = Number(proposal?.totalAttachments ?? 0);
  const coverLetter = proposal?.coverLetter?.trim();
  const hasCoverLetter = Boolean(coverLetter && coverLetter.length > 0);

  const submittedAtDate = proposal?.submittedAt;
  const relativeTimeStr = submittedAtDate
    ? dayjs(submittedAtDate).tz().locale(locale === 'ar' ? 'ar' : 'en').fromNow()
    : '-';

  return (
    <div
      className={`space-y-4 rounded-[16px] bg-white dark:bg-dark-surface-primary `}
      data-testid="buyer-proposal-basic-card"
      id={proposal?.id}
    >
      {/* Top Profile Header */}
      <BasicProfileCard
        name={proposal?.profile?.name}
        photo={proposal?.profile?.photoUrl}
        rate={proposal?.profile?.rating}
        reviews={proposal?.profile?.reviewsCount}
        orders={proposal?.profile?.ordersCount}
        avgResponse={proposal?.profile?.avgResponse}
        tag={proposal?.statuses?.[0]?.name}
        t={t}
      />

      {/* Cover Letter Section */}
      {hasCoverLetter && (
        <div
          className="space-y-1.5 pt-1"
          data-testid="buyer-proposal-cover-letter-section"
        >
          <div
            className="flex items-center gap-2"
            data-testid="buyer-proposal-cover-letter-header"
          >
            <MessageText1
              variant="Bold"
              className="w-4 h-4 text-light-text-primary dark:text-dark-text-primary"
            />
            <InfoText
              text={t('postDetails.coverLetter')}
              className="!font-bold !text-[15px] sm:!text-[16px] !text-light-text-primary dark:!text-dark-text-primary"
              data-testid="buyer-proposal-cover-letter-label"
            />
          </div>

          <InfoText
            text={proposal?.coverLetter || ''}
            maxLength="FULL"
            className="!text-[14px] sm:!text-[15px] !leading-[150%] !font-normal !text-light-text-primary dark:!text-dark-text-primary tracking-normal break-words"
            data-testid="buyer-proposal-cover-letter-text"
          />
        </div>
      )}

      {/* Bottom Row: Left has Pricing & Delivery, Right has Attachments & Submitted time */}
      <div
        className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        data-testid="buyer-proposal-bottom-row"
      >
        {/* Left: Total price & Delivery time */}
        <div
          className="flex items-center gap-5 sm:gap-8"
          data-testid="buyer-proposal-pricing-block"
        >
          {/* Total Price */}
          <div data-testid="buyer-proposal-total-price">
            <InfoText
              text={t('postDetails.totalPrice')}
              className="!text-[14px] !leading-[100%] !font-normal !text-light-text-primary dark:!text-dark-text-primary tracking-normal block mb-1"
            />
            <div className="flex items-center gap-1.5 text-light-information dark:text-dark-information !font-bold !text-[20px] sm:!text-[22px] !leading-[100%] tracking-normal">
              <MessageText1 size={20} variant="Bold" className="text-light-information dark:text-dark-information" />
              <InfoText
                text={`${formattedPrice} ${currencyDisplay}`}
                className="!font-bold !text-[20px] sm:!text-[22px] !leading-[100%] tracking-normal !text-light-information dark:!text-dark-information"
                data-testid="buyer-proposal-total-price-text"
              />
            </div>
          </div>

          {/* Divider */}
          <div className="w-[1px] h-8 bg-light-input-border dark:bg-dark-input-border" />

          {/* Delivery Time */}
          <div data-testid="buyer-proposal-delivery-time">
            <InfoText
              text={t('postDetails.deliveryTime')}
              className="!text-[14px] !leading-[100%] !font-normal !text-light-text-primary dark:!text-dark-text-primary tracking-normal block mb-1"
            />
            <div className="flex items-center gap-1.5 text-light-alert dark:text-dark-alert !font-bold !text-[20px] sm:!text-[22px] !leading-[100%] tracking-normal">
              <MessageText1 size={20} variant="Bold" className="text-light-alert dark:text-dark-alert" />
              <InfoText
                text={formattedDelivery}
                className="!font-bold !text-[20px] sm:!text-[22px] !leading-[100%] tracking-normal !text-light-alert dark:!text-dark-alert"
                data-testid="buyer-proposal-delivery-time-text"
              />
            </div>
          </div>
        </div>

        {/* Right: Attachments count (if any) & Submitted at */}
        <div
          className="flex items-center gap-3 sm:gap-5"
          data-testid="buyer-proposal-meta-right"
        >
          {attachments > 0 && (
            <div
              className="px-3 py-1.5 h-[34px] flex items-center gap-1.5 bg-soft-background dark:bg-dark-surface-secondary border border-light-input-border dark:border-dark-input-border rounded-[8px] select-none"
              data-testid="buyer-proposal-attachments-count"
            >
              <AttachCircle size={16} variant="Bold" className="text-light-text-primary dark:text-dark-text-primary" />
              <InfoText
                text={t('postDetails.attachmentsCount', {
                  count: attachments,
                })}
                className="!text-[13px] !leading-[100%] !font-normal !text-light-text-primary dark:!text-dark-text-primary tracking-normal"
              />
            </div>
          )}

          <InfoText
            text={t('postDetails.submittedAt', {
              time: relativeTimeStr,
            })}
            className="!text-[14px] !leading-[20px] !font-normal !text-light-text-secondary dark:text-dark-text-secondary tracking-normal"
            data-testid="buyer-proposal-submitted-at"
          />
        </div>
      </div>
    </div>
  );
}
